import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, CircularProgress, Alert, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, Tooltip, Snackbar, FormControlLabel, Switch,
  Checkbox, Divider, InputAdornment
} from '@mui/material';
import {
  Add, Edit, Delete, Visibility, CalendarToday, WaterDrop, 
  Info, CheckCircle, Warning, Close, Agriculture, Terrain, Lock,
  Verified, Shield, TrendingUp, AccessTime, CheckCircleOutline
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { dashboardAPI, pondsAPI, farmsAPI, stockingEventsAPI, policiesAPI } from 'services/api';
import DOCProgressIndicator from 'shared/components/DOCProgressIndicator';
import { getMaxStockingDensity, validateStockingDensity } from 'shared/utils/caaLimits';

const StockingDetails = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stockingEvents, setStockingEvents] = useState([]);
  const [ponds, setPonds] = useState([]);
  const [farms, setFarms] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    pond_id: '',
    stocking_date: '',
    species: 'Vannamei',
    stocking_density_per_m2: '',
    spf_seed: false,
    hatchery_name: '',
    seed_quantity: '',
    seed_size: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  // Auto-fill pond if only 1 pond exists and form is open
  useEffect(() => {
    if (openDialog && !editingEvent && ponds.length === 1 && !formData.pond_id) {
      setFormData(prev => ({ ...prev, pond_id: ponds[0].id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, ponds.length, editingEvent]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load farms and ponds
      try {
        const bulkDataResponse = await dashboardAPI.getFarmerBulkData();
        const bulkData = bulkDataResponse.data;
        setFarms(bulkData.farms);
        const allPonds = bulkData.farms.flatMap(farm => farm.ponds || []);
        setPonds(allPonds);
      } catch (bulkError) {
        console.warn('Bulk data endpoint failed, using fallback method:', bulkError);
        const farmsResponse = await farmsAPI.getAll();
        const farms = farmsResponse.data;
        setFarms(farms);
        
        const allPonds = [];
        for (const farm of farms) {
          try {
            const pondResponse = await pondsAPI.getByFarm(farm.id);
            allPonds.push(...pondResponse.data);
          } catch (pondError) {
            console.warn(`Failed to load ponds for farm ${farm.id}:`, pondError);
          }
        }
        setPonds(allPonds);
      }
      
      // Load stocking events
      try {
        const eventsResponse = await stockingEventsAPI.getAll();
        setStockingEvents(eventsResponse.data || []);
      } catch (eventsError) {
        console.error('Error loading stocking events:', eventsError);
        // Don't fail completely if stocking events fail - just show empty list
        setStockingEvents([]);
        if (eventsError.response?.status === 404 || eventsError.response?.status === 403) {
          // User might not have any stocking events yet - that's okay
          console.log('No stocking events found or access denied');
        } else {
          setError(`Failed to load stocking events: ${eventsError.response?.data?.detail || eventsError.message}`);
        }
      }
      
      // Load policies to check for active policies (for edit disable logic)
      try {
        const policiesResponse = await policiesAPI.getAll();
        setPolicies(policiesResponse.data || []);
      } catch (policiesError) {
        console.warn('Error loading policies:', policiesError);
        setPolicies([]);
      }
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(`Failed to load data: ${err.response?.data?.detail || err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateDOC = (stockingDate) => {
    if (!stockingDate) return null;
    const today = new Date();
    const stockDate = new Date(stockingDate);
    const diffTime = Math.abs(today - stockDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const calculateDaysRemaining = (stockingDate, targetDOC) => {
    if (!stockingDate || !targetDOC) return null;
    const currentDOC = calculateDOC(stockingDate);
    if (currentDOC === null) return null;
    return Math.max(0, targetDOC - currentDOC);
  };

  const getSpeciesInfo = (species) => {
    const speciesMap = {
      'Vannamei': { name: 'Penaeus vannamei', common: 'Whiteleg Shrimp', icon: '🦐' },
      'Monodon': { name: 'Penaeus monodon', common: 'Black Tiger Shrimp', icon: '🦐' },
      'Indicus': { name: 'Penaeus indicus', common: 'Indian White Shrimp', icon: '🦐' },
      'Scampi': { name: 'Macrobrachium rosenbergii', common: 'Giant Freshwater Prawn', icon: '🦐' }
    };
    return speciesMap[species] || { name: species, common: species, icon: '🦐' };
  };

  const isFormValid = () => {
    return !!(formData.pond_id && formData.stocking_date && formData.species && formData.stocking_density_per_m2 && !fieldErrors.stocking_density_per_m2);
  };

  /**
   * DATA MODEL ASSUMPTIONS:
   * - All logic and API calls use IDs (pond_id, farm_id, farmer_id, stocking_id)
   * - Names are ONLY used for UI display
   * - Relationships: Pond → Farm → Farmer (always use IDs to traverse)
   */

  /**
   * Get pond name for display only.
   * @param {number} pondId - The pond ID (never use name as identifier)
   * @returns {string} Pond name for UI display
   */
  const getPondName = (pondId) => {
    const pond = ponds.find(p => p.id === pondId);
    return pond ? pond.pond_name : `Pond #${pondId}`;
  };

  /**
   * Get farm name for display only.
   * Uses pond_id to find pond, then pond.farm_id to find farm.
   * @param {number} pondId - The pond ID (never use name as identifier)
   * @returns {string} Farm name for UI display
   */
  const getFarmName = (pondId) => {
    const pond = ponds.find(p => p.id === pondId);
    if (!pond) return 'Unknown';
    const farm = farms.find(f => f.id === pond.farm_id);
    return farm ? farm.farm_name : 'Unknown Farm';
  };

  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /**
   * Check if a pond has an active policy.
   * Uses pond_id for logic (never use names).
   * @param {number} pondId - The pond ID
   * @returns {boolean} True if pond has an active policy
   */
  const hasActivePolicy = (pondId) => {
    return policies.some(policy => 
      policy.pond_id === pondId && 
      (policy.status === 'active' || policy.status === 'ACTIVE')
    );
  };

  /**
   * View stocking event details.
   * Uses event object which contains IDs (event.id, event.pond_id).
   * @param {Object} event - Stocking event object with IDs
   */
  const handleViewDetails = (event) => {
    setViewingEvent(event);
    setViewDetailsDialog(true);
  };

  const handleCloseViewDetails = () => {
    setViewDetailsDialog(false);
    setViewingEvent(null);
  };

  /**
   * Open dialog for create/edit.
   * Uses event.pond_id (ID) for form data, never event.pond_name.
   * @param {Object|null} event - Stocking event object with IDs, or null for new event
   */
  const handleOpenDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        pond_id: event.pond_id || '', // Always use ID, never name
        stocking_date: event.stocking_date ? event.stocking_date.split('T')[0] : '',
        species: event.species || 'Vannamei',
        stocking_density_per_m2: event.stocking_density_per_m2 || event.stocking_density || '',
        spf_seed: event.spf_seed || false,
        hatchery_name: event.hatchery_name || '',
        seed_quantity: event.seed_quantity || '',
        seed_size: event.seed_size || '',
      });
    } else {
      setEditingEvent(null);
      // Auto-fill pond if only 1 pond exists
      const autoPondId = ponds.length === 1 ? ponds[0].id : '';
      setFormData({
        pond_id: autoPondId,
        stocking_date: getCurrentDate(),
        species: 'Vannamei',
        stocking_density_per_m2: '',
        spf_seed: false,
        hatchery_name: '',
        seed_quantity: '',
        seed_size: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    setFormData({
      pond_id: '',
      stocking_date: '',
      species: 'Vannamei',
      stocking_density_per_m2: '',
      spf_seed: false,
      hatchery_name: '',
      seed_quantity: '',
      seed_size: '',
    });
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      console.log('handleSubmit called with formData:', formData);
      console.log('editingEvent:', editingEvent);
      
      // Validate required fields
      if (!formData.pond_id || !formData.stocking_date || !formData.stocking_density_per_m2) {
        console.log('Validation failed:', { 
          pond_id: formData.pond_id, 
          stocking_date: formData.stocking_date, 
          stocking_density_per_m2: formData.stocking_density_per_m2 
        });
        setSnackbar({ 
          open: true, 
          message: 'Please fill in all required fields (Pond, Stocking Date, and Density)', 
          severity: 'error' 
        });
        return;
      }

      // Validate stocking density against CAA limits
      const density = parseFloat(formData.stocking_density_per_m2);
      const validation = validateStockingDensity(formData.species, density);
      if (!validation.isValid) {
        setFieldErrors({
          ...fieldErrors,
          stocking_density_per_m2: validation.errorMessage
        });
        setSnackbar({
          open: true,
          message: validation.errorMessage,
          severity: 'error'
        });
        return;
      }

      // Always use IDs in API payloads, never names
      const submitData = {
        pond_id: parseInt(formData.pond_id), // ID, not name
        stocking_date: formData.stocking_date,
        species: formData.species,
        stocking_density_per_m2: parseFloat(formData.stocking_density_per_m2),
        spf_seed: formData.spf_seed,
        hatchery_name: formData.hatchery_name || null,
        seed_quantity: formData.seed_quantity ? parseInt(formData.seed_quantity) : null,
        seed_size: formData.seed_size || null,
      };

      console.log('Submitting data:', submitData);

      if (editingEvent) {
        console.log('Updating event:', editingEvent.id); // Using ID
        await stockingEventsAPI.update(editingEvent.id, submitData); // ID for update
        setSnackbar({ open: true, message: 'Stocking event updated successfully', severity: 'success' });
      } else {
        console.log('Creating new event');
        const response = await stockingEventsAPI.create(submitData);
        console.log('Create response:', response);
        setSnackbar({ open: true, message: 'Stocking event created successfully', severity: 'success' });
      }
      
      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Error saving stocking event:', err);
      console.error('Error response:', err.response);
      console.error('Error details:', err.response?.data);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to save stocking event. Please try again.';
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    }
  };

  /**
   * Delete a stocking event.
   * Uses eventId (ID) for API call, never use name.
   * @param {number} eventId - The stocking event ID
   */
  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this stocking event? This action cannot be undone.')) {
      return;
    }

    try {
      await stockingEventsAPI.delete(eventId); // Always use ID
      setSnackbar({ open: true, message: 'Stocking event deleted successfully', severity: 'success' });
      loadData();
    } catch (err) {
      console.error('Error deleting stocking event:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.detail || 'Failed to delete stocking event. Please try again.', 
        severity: 'error' 
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Stocking Details
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Stocking Event
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Important:</strong> Stocking events are critical for policy and claim calculations. 
          DOC (Days of Culture) is automatically calculated from the stocking date. 
          Without proper stocking records, premium and claim logic will break.
        </Typography>
      </Alert>

      {error && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-icon': {
              color: 'warning.main'
            }
          }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Pond</strong></TableCell>
                  {farms.length > 1 && <TableCell><strong>Farm</strong></TableCell>}
                  <TableCell><strong>Stocking Date</strong></TableCell>
                  <TableCell><strong>Species</strong></TableCell>
                  <TableCell><strong>Density (per m²)</strong></TableCell>
                  <TableCell><strong>SPF Seed</strong></TableCell>
                  <TableCell><strong>DOC</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stockingEvents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={farms.length > 1 ? 8 : 7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No stocking events found. Click "Add Stocking Event" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  stockingEvents.map((event) => {
                    const doc = calculateDOC(event.stocking_date);
                    const pondHasActivePolicy = hasActivePolicy(event.pond_id);
                    return (
                      <TableRow key={event.id} hover>
                        <TableCell>{getPondName(event.pond_id)}</TableCell>
                        {farms.length > 1 && (
                        <TableCell>{getFarmName(event.pond_id)}</TableCell>
                        )}
                        <TableCell>
                          {event.stocking_date ? new Date(event.stocking_date).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>{event.species || 'N/A'}</TableCell>
                        <TableCell>{event.stocking_density_per_m2 || event.stocking_density || 'N/A'}</TableCell>
                        <TableCell>
                          {event.spf_seed ? (
                            <Chip label="Yes" color="success" size="small" icon={<CheckCircle />} />
                          ) : (
                            <Chip label="No" color="default" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          {event.stocking_date && doc !== null ? (
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              DOC: {doc}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">N/A</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewDetails(event)} color="primary">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={pondHasActivePolicy ? "Cannot edit after policy issuance" : "Edit"}>
                            <span>
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenDialog(event)}
                                disabled={pondHasActivePolicy}
                              >
                              <Edit />
                            </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(event.id)} color="error">
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog - Professional Design */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            maxHeight: '90vh',
            width: '95%',
            maxWidth: '1200px'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1.5, 
          pt: 2,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Agriculture sx={{ fontSize: 24 }} />
            <Box>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {editingEvent ? 'Edit Stocking Event' : 'Add New Stocking Event'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                Record shrimp stocking details
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            size="small"
            sx={{
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          bgcolor: '#f5f7fa', 
          height: 'auto',
          maxHeight: 'calc(90vh - 140px)', 
          overflow: 'hidden', 
          display: 'flex', 
          flexDirection: 'column',
          '@media (max-width: 960px)': {
            height: 'auto',
            maxHeight: 'calc(90vh - 140px)',
            overflow: 'auto'
          }
        }}>
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '70% 30%' },
            alignItems: 'start',
            height: 'auto',
            flex: 1,
            overflow: 'hidden',
            gap: 0
          }}>
            {/* Left Column: Form Inputs (70%) */}
            <Box sx={{ 
              pt: 2.5,
              px: 2.5,
              pb: 0,
              overflow: { xs: 'auto', md: 'auto' },
              borderRight: { md: '1px solid #e0e7ef' }, 
              alignSelf: 'start',
              height: 'auto',
              maxHeight: { xs: 'calc(90vh - 200px)', md: 'calc(90vh - 200px)' },
              '@media (max-width: 960px)': {
                borderRight: 'none'
              }
            }}>
              <Box sx={{ maxWidth: '100%', pr: { md: 1 }, pb: 0, mb: 0 }}>
                {/* Section: Pond & Date */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500, display: 'block', mb: 1 }}>
                    Pond & Date
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={8}>
                      <FormControl fullWidth required sx={{ 
                        '& .MuiInputBase-root': { height: '44px' },
                        '& .MuiSelect-select': { height: '44px', display: 'flex', alignItems: 'center', py: 0 },
                        '& .MuiFormLabel-asterisk': { color: 'text.secondary' }
                      }}>
                        <InputLabel>Select Pond *</InputLabel>
                <Select
                  value={formData.pond_id || ''}
                          label="Select Pond *"
                          onChange={(e) => setFormData({ ...formData, pond_id: e.target.value })}
                          error={false}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: !formData.pond_id ? 'rgba(0, 0, 0, 0.23)' : 'rgba(0, 0, 0, 0.23)'
                            }
                          }}
                          renderValue={(value) => {
                            const pond = ponds.find(p => p.id === value);
                            if (!pond) return '';
                            const farmName = getFarmName(pond.id);
                            return `${pond.pond_name}${farmName && farmName !== 'Unknown' ? ` • ${farmName}` : ''} • ${pond.pond_area ? `${parseFloat(pond.pond_area).toFixed(2)} ha` : 'N/A'}`;
                  }}
                >
                  {ponds.length === 0 ? (
                    <MenuItem disabled>No ponds available</MenuItem>
                  ) : (
                    ponds.map((pond) => (
                      <MenuItem key={pond.id} value={pond.id}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {pond.pond_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getFarmName(pond.id)} • {pond.pond_area ? `${parseFloat(pond.pond_area).toFixed(2)} ha` : 'N/A'}
                            </Typography>
                          </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

                    <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                        label="Stocking Date *"
                value={formData.stocking_date || ''}
                onChange={(e) => {
                          const selectedDate = e.target.value;
                          if (user?.role === 'farmer' && selectedDate) {
                            const today = new Date().toISOString().split('T')[0];
                            if (selectedDate < today) {
                              setSnackbar({
                                open: true,
                                message: 'Farmers cannot select past dates for stocking. Only today or future dates are allowed.',
                                severity: 'error'
                              });
                              return;
                            }
                          }
                          setFormData({ ...formData, stocking_date: selectedDate });
                }}
                InputLabelProps={{ shrink: true }}
                required
                        error={false}
                        helperText={user?.role === 'farmer' ? 'Cannot select past dates' : ''}
                        inputProps={{
                          min: user?.role === 'farmer' ? getCurrentDate() : undefined
                        }}
                        sx={{ 
                          '& .MuiInputBase-root': { height: '44px' },
                          '& .MuiInputBase-input': { height: '44px', py: 0 },
                          '& .MuiFormLabel-asterisk': { color: 'text.secondary' },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: !formData.stocking_date ? 'rgba(0, 0, 0, 0.23)' : 'rgba(0, 0, 0, 0.23)'
                          }
                        }}
              />
            </Grid>
                  </Grid>
                </Box>

                {/* Section: Stocking Details */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500, display: 'block', mb: 1 }}>
                    Stocking Details
                  </Typography>
                  <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required sx={{ 
                        '& .MuiInputBase-root': { height: '44px' },
                        '& .MuiSelect-select': { height: '44px', display: 'flex', alignItems: 'center', py: 0 },
                        '& .MuiInputLabel-root': { whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }
                      }}>
                        <InputLabel>Species *</InputLabel>
                <Select
                  value={formData.species}
                          label="Species *"
                          onChange={(e) => {
                            const newSpecies = e.target.value;
                            const maxDensity = getMaxStockingDensity(newSpecies);
                            const currentDensity = parseFloat(formData.stocking_density_per_m2);
                            
                            const newErrors = { ...fieldErrors };
                            delete newErrors.stocking_density_per_m2;
                            
                            if (currentDensity && maxDensity && currentDensity > maxDensity) {
                              newErrors.stocking_density_per_m2 = `Maximum allowed: ${maxDensity}/m² (CAA limit for ${newSpecies})`;
                            }
                            
                            setFieldErrors(newErrors);
                            setFormData({ ...formData, species: newSpecies });
                          }}
                          renderValue={(value) => {
                            if (!value) return '';
                            const speciesInfo = getSpeciesInfo(value);
                            return speciesInfo.name;
                          }}
                        >
                      <MenuItem value="Vannamei">
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Penaeus vannamei
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Whiteleg Shrimp
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Monodon">
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Penaeus monodon
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Black Tiger Shrimp
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Indicus">
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Penaeus indicus
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Indian White Shrimp
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Scampi">
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Macrobrachium rosenbergii
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Giant Freshwater Prawn
                          </Typography>
                        </Box>
                      </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                        label="Stocking Density (per m²) *"
                value={formData.stocking_density_per_m2 || ''}
                        InputLabelProps={{
                          sx: { whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }
                        }}
                onChange={(e) => {
                          const value = e.target.value;
                          const density = parseFloat(value);
                          const maxDensity = getMaxStockingDensity(formData.species);
                          if (value && !isNaN(density) && maxDensity && density > maxDensity) {
                            setFieldErrors({
                              ...fieldErrors,
                              stocking_density_per_m2: `Maximum allowed: ${maxDensity}/m² (CAA limit for ${formData.species})`
                            });
                          } else {
                            const validation = validateStockingDensity(formData.species, density);
                            if (!validation.isValid && value) {
                              setFieldErrors({
                                ...fieldErrors,
                                stocking_density_per_m2: validation.errorMessage
                              });
                            } else {
                              const newErrors = { ...fieldErrors };
                              delete newErrors.stocking_density_per_m2;
                              setFieldErrors(newErrors);
                            }
                          }
                          setFormData({ ...formData, stocking_density_per_m2: value });
                }}
                required
                        error={false}
                        placeholder="Enter density (per m²)"
                        helperText={
                          fieldErrors.stocking_density_per_m2 
                            ? fieldErrors.stocking_density_per_m2
                            : formData.species 
                              ? `CAA Max: ${getMaxStockingDensity(formData.species) || 'N/A'}/m²`
                              : 'Enter density (per m²)'
                        }
                        inputProps={{ 
                          min: 0, 
                          max: getMaxStockingDensity(formData.species) || undefined,
                          step: 0.01 
                        }}
                        sx={{ 
                          '& .MuiInputBase-root': { height: '44px' },
                          '& .MuiInputBase-input': { 
                            height: '44px', 
                            py: 0,
                            width: '100% !important',
                            '&:invalid': {
                              width: '100% !important',
                              boxShadow: 'none'
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            width: '100% !important',
                            '&:invalid': {
                              width: '100% !important',
                              boxShadow: 'none'
                            }
                          },
                          '& .MuiFormLabel-asterisk': { color: 'text.secondary' },
                          '& .MuiFormHelperText-root': {
                            mt: 0.5,
                            mx: 0,
                            fontSize: '0.75rem',
                            color: fieldErrors.stocking_density_per_m2 ? 'warning.main' : 'text.secondary',
                            order: 2,
                            mb: 0
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: fieldErrors.stocking_density_per_m2 ? 'warning.main' : 'rgba(0, 0, 0, 0.23)',
                            borderWidth: fieldErrors.stocking_density_per_m2 ? '1px' : '1px'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: fieldErrors.stocking_density_per_m2 ? 'warning.main' : 'rgba(0, 0, 0, 0.87)'
                          },
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: fieldErrors.stocking_density_per_m2 ? 'warning.main' : 'primary.main',
                            borderWidth: fieldErrors.stocking_density_per_m2 ? '2px' : '2px'
                          },
                          mb: 0
                        }}
                      />
            </Grid>
                  </Grid>
                </Box>

                {/* Section: Seed Quality & Source */}
                <Box sx={{ mb: 0 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500, display: 'block', mb: 1 }}>
                    Seed Quality & Source
                  </Typography>
                  <Grid container spacing={1.5} sx={{ mb: 0, '& > .MuiGrid-item': { mb: 0 } }}>
                    <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Hatchery Name"
                        value={formData.hatchery_name || ''}
                onChange={(e) => setFormData({ ...formData, hatchery_name: e.target.value })}
                        placeholder="Enter hatchery name"
                        helperText="Optional"
                        sx={{ 
                          '& .MuiInputBase-root': { height: '44px' },
                          '& .MuiInputBase-input': { height: '44px', py: 0 },
                          '& .MuiFormHelperText-root': {
                            mt: 0.5,
                            mx: 0,
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            order: 2
                          }
                        }}
              />
            </Grid>
                    <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Seed Quantity"
                type="number"
                        value={formData.seed_quantity || ''}
                onChange={(e) => setFormData({ ...formData, seed_quantity: e.target.value })}
                inputProps={{ min: 0 }}
                        placeholder="Total number of seeds"
                        helperText="Optional"
                        sx={{ 
                          '& .MuiInputBase-root': { height: '44px', width: '100%' },
                          '& .MuiInputBase-input': { 
                            height: '44px', 
                            py: 0,
                            width: '100% !important',
                            '&:invalid': {
                              width: '100% !important',
                              boxShadow: 'none'
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            width: '100% !important',
                            '&:invalid': {
                              width: '100% !important',
                              boxShadow: 'none'
                            }
                          },
                          '& .MuiFormHelperText-root': {
                            mt: 0.5,
                            mx: 0,
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            order: 2
                          }
                        }}
              />
            </Grid>
                    <Grid item xs={12} sm={4} sx={{ mb: 0 }}>
              <TextField
                fullWidth
                        label="Seed Size"
                        value={formData.seed_size || ''}
                onChange={(e) => setFormData({ ...formData, seed_size: e.target.value })}
                        placeholder="e.g., PL10, PL12"
                        helperText="Optional"
                        sx={{ 
                          '& .MuiInputBase-root': { height: '44px' },
                          '& .MuiInputBase-input': { height: '44px', py: 0 },
                          '& .MuiFormHelperText-root': {
                            mt: 0.5,
                            mx: 0,
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            order: 2,
                            mb: 0
                          },
                          mb: 0
                        }}
              />
            </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>

            {/* Right Column: Insurance & Compliance Info (30%) */}
            <Box sx={{ 
              p: 2.5, 
              bgcolor: '#fafbfc', 
              overflow: { xs: 'auto', md: 'auto' },
              alignSelf: 'start',
              height: 'auto',
              maxHeight: { xs: 'calc(90vh - 200px)', md: 'calc(90vh - 200px)' },
              '@media (max-width: 960px)': {
                borderTop: '1px solid #e0e7ef',
                mt: 2
              }
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* CAA Compliance Card */}
                {formData.species && (
                  <Card sx={{ 
                    p: 1.5, 
                    bgcolor: '#ffffff', 
                    border: '1px solid #e0e7ef', 
                    borderRadius: 1,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                      <Verified sx={{ color: 'primary.main', fontSize: 16 }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.primary' }}>
                        CAA Compliance
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.75, lineHeight: 1.4 }}>
                      Max density: <strong>{getMaxStockingDensity(formData.species) || 'N/A'}/m²</strong>
                    </Typography>
                    {formData.stocking_density_per_m2 && (
                      <Box>
                        {parseFloat(formData.stocking_density_per_m2) > (getMaxStockingDensity(formData.species) || 0) ? (
                          <Chip 
                            label="Exceeds Limit" 
                            size="small" 
                            color="warning" 
                            sx={{ fontSize: '0.65rem', height: 20, fontWeight: 500 }}
                          />
                        ) : (
                          <Chip 
                            label="Within Limit" 
                            size="small" 
                            color="success" 
                            icon={<CheckCircle sx={{ fontSize: 11 }} />}
                            sx={{ fontSize: '0.65rem', height: 20, fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    )}
                  </Card>
                )}

                {/* DOC Info Card */}
            {formData.stocking_date && (
                  <Card sx={{ 
                    p: 1.5, 
                    bgcolor: '#ffffff', 
                    border: '1px solid #e0e7ef', 
                    borderRadius: 1,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                      <AccessTime sx={{ color: 'primary.main', fontSize: 16 }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.primary' }}>
                        DOC Information
                  </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.4 }}>
                      DOC starts from selected date and cannot be edited after policy issuance.
                    </Typography>
                  </Card>
                )}

                {/* SPF Seed Card - Policy Option */}
                <Card sx={{ 
                  p: 1.5, 
                  bgcolor: formData.spf_seed ? '#e8f5e9' : '#ffffff', 
                  border: `1px solid ${formData.spf_seed ? '#4caf50' : '#e0e7ef'}`,
                  borderRadius: 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: formData.spf_seed ? '#4caf50' : '#bdbdbd'
                  }
                }}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                    <Shield sx={{ color: formData.spf_seed ? 'success.main' : 'primary.main', fontSize: 16 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.primary' }}>
                      SPF Seed Option
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
                    <Box display="flex" alignItems="center" gap={1} flex={1}>
                      <Switch
                        checked={formData.spf_seed}
                        onChange={(e) => setFormData({ ...formData, spf_seed: e.target.checked })}
                        color="success"
                        size="small"
                      />
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.4 }}>
                        Specific Pathogen Free
                      </Typography>
                    </Box>
                    {formData.spf_seed && (
                      <Chip 
                        label="15%" 
                        size="small" 
                        color="success" 
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: formData.spf_seed ? 'success.dark' : 'text.secondary', lineHeight: 1.4 }}>
                    {formData.spf_seed ? '15% premium discount applied' : 'Eligible for 15% premium discount'}
                  </Typography>
                </Card>

                {/* Data Lock Notice */}
                <Card sx={{ 
                  p: 1.5, 
                  bgcolor: '#fff9e6', 
                  border: '1px solid #ffd54f', 
                  borderRadius: 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}>
                  <Box display="flex" alignItems="start" gap={1}>
                    <Lock sx={{ color: '#f57c00', fontSize: 14, mt: 0.25, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#e65100', lineHeight: 1.4 }}>
                      Stocking details cannot be edited after policy issuance.
                    </Typography>
                  </Box>
                </Card>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          px: 2.5, 
          py: 1.5, 
          bgcolor: '#ffffff', 
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.05)',
          mt: 'auto',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: { xs: 1, sm: 0 },
          justifyContent: 'flex-end'
        }}>
          <Button 
            onClick={handleCloseDialog} 
            variant="outlined"
            size="medium"
            sx={{ 
              minWidth: { xs: '100%', sm: 100 }, 
              mr: { xs: 0, sm: 1.5 },
              order: { xs: 1, sm: 1 },
              mb: { xs: 1, sm: 0 }
            }}
          >
            Cancel
          </Button>
          <Tooltip title={!isFormValid() ? 'Please complete all required fields' : ''} arrow>
            <span>
          <Button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(e);
            }} 
            variant="contained" 
                disabled={!isFormValid()}
                size="medium"
                sx={{ 
                  minWidth: { xs: '100%', sm: 180 }, 
                  fontWeight: 600,
                  order: { xs: 2, sm: 2 },
                  '&.Mui-disabled': {
                    backgroundColor: 'action.disabledBackground',
                    color: 'action.disabled'
                  }
                }}
                startIcon={editingEvent ? <Edit /> : <Add />}
              >
                {editingEvent ? 'Update Event' : 'Create Stocking Event'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDetailsDialog}
        onClose={handleCloseViewDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            '@media (max-width: 960px)': {
              maxHeight: '95vh'
            }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Stocking Event Details
          </Typography>
          <IconButton size="small" onClick={handleCloseViewDetails}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          overflow: { xs: 'auto', md: 'auto' },
          flex: '1 1 auto',
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 }
        }}>
          {viewingEvent && (() => {
            const currentDOC = calculateDOC(viewingEvent.stocking_date);
            const pond = ponds.find(p => p.id === viewingEvent.pond_id);
            const targetDOC = pond?.doc_requested || 120;
            const daysRemaining = currentDOC !== null ? Math.max(0, targetDOC - currentDOC) : null;
            const waitingPeriodDays = 7; // Default waiting period
            const variableCostEligibleDays = 15; // Variable cost applies after 15 days
            const isClaimEligible = currentDOC !== null && currentDOC > waitingPeriodDays;
            const isVariableCostEligible = currentDOC !== null && currentDOC >= variableCostEligibleDays;
            const pondHasActivePolicy = hasActivePolicy(viewingEvent.pond_id);
            
            return (
              <Box sx={{ mt: 1 }}>
                {/* Lock Notice */}
                {pondHasActivePolicy && (
                  <Alert severity="warning" sx={{ mb: 3, bgcolor: '#fff3cd', border: '1px solid #ffc107' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Stocking details are locked after policy issuance.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      This stocking event is associated with an active policy and cannot be modified.
                    </Typography>
                  </Alert>
                )}

                {/* Read-only Identifiers Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                    Stocking Event Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Farmer
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user?.name || user?.email || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Farm Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {getFarmName(viewingEvent.pond_id)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Pond Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {getPondName(viewingEvent.pond_id)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Stocking Date
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewingEvent.stocking_date ? new Date(viewingEvent.stocking_date).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Species
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewingEvent.species ? getSpeciesInfo(viewingEvent.species).name : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Stocking Density (per m²)
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewingEvent.stocking_density_per_m2 || viewingEvent.stocking_density || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        SPF Seed Status
                      </Typography>
                      <Box>
                        {viewingEvent.spf_seed ? (
                          <Chip label="Yes" color="success" size="small" icon={<CheckCircle />} />
                        ) : (
                          <Chip label="No" color="default" size="small" />
                        )}
                      </Box>
                    </Grid>
                    {viewingEvent.hatchery_name && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Hatchery Name
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewingEvent.hatchery_name}
                        </Typography>
                      </Grid>
                    )}
                    {viewingEvent.seed_quantity && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Seed Quantity
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewingEvent.seed_quantity}
                        </Typography>
                      </Grid>
                    )}
                    {viewingEvent.seed_size && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Seed Size
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewingEvent.seed_size}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {/* Full DOC Card Section */}
                {viewingEvent.stocking_date && currentDOC !== null && (
                  <Box sx={{ mb: 3 }}>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                      Days of Culture (DOC) Progress
                    </Typography>
                    <Card sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e7ef', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                        <DOCProgressIndicator
                          stockingDate={viewingEvent.stocking_date}
                          targetDOC={targetDOC}
                          variant="circular"
                          size="large"
                          showDetails={true}
                          label="DOC Progress"
                        />
                      </Box>
                      
                      {/* DOC Statistics */}
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Days Elapsed
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                              {currentDOC}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Days Remaining
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: daysRemaining > 0 ? 'success.main' : 'text.secondary' }}>
                              {daysRemaining !== null ? daysRemaining : 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Target DOC
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              {targetDOC}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Claim Eligibility Indicator */}
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Shield sx={{ fontSize: 18 }} />
                          Claim Eligibility Status
                        </Typography>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <Card sx={{ 
                              p: 1.5, 
                              bgcolor: isClaimEligible ? '#e8f5e9' : '#fff3cd', 
                              border: `1px solid ${isClaimEligible ? '#4caf50' : '#ffc107'}`,
                              borderRadius: 1
                            }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                {isClaimEligible ? (
                                  <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                ) : (
                                  <Warning sx={{ color: 'warning.main', fontSize: 20 }} />
                                )}
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {isClaimEligible ? 'Eligible for Claims' : `Waiting Period (${waitingPeriodDays} days)`}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {isClaimEligible 
                                      ? 'Claims can be filed (loss ≥ 70% required)'
                                      : `Claims cannot be filed until day ${waitingPeriodDays + 1}`}
                                  </Typography>
                                </Box>
                              </Box>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Card sx={{ 
                              p: 1.5, 
                              bgcolor: isVariableCostEligible ? '#e3f2fd' : '#f5f5f5', 
                              border: `1px solid ${isVariableCostEligible ? '#2196f3' : '#e0e0e0'}`,
                              borderRadius: 1
                            }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                {isVariableCostEligible ? (
                                  <TrendingUp sx={{ color: 'primary.main', fontSize: 20 }} />
                                ) : (
                                  <AccessTime sx={{ color: 'text.secondary', fontSize: 20 }} />
                                )}
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Variable Cost Eligibility
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {isVariableCostEligible 
                                      ? 'Variable cost applies (DOC ≥ 16)'
                                      : `Variable cost applies from day ${variableCostEligibleDays}`}
                                  </Typography>
                                </Box>
                              </Box>
                            </Card>
                          </Grid>
                        </Grid>
                      </Box>
                    </Card>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseViewDetails} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
};

export default StockingDetails;

